import { useState } from 'react';

export default function LawEnforcementSection() {
  const [activeTheory, setActiveTheory] = useState<'broken-windows' | 'crime-triangle'>('broken-windows');

  return (
    <section className="py-20 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-teal-500/20 text-teal-400 rounded-full text-sm font-medium mb-4">
            Law Enforcement Foundation
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Decades of Law Enforcement Experience
          </h2>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            SafePoint's approach is built on proven criminological theories and real world law enforcement experience, 
            bringing professional grade security assessment to every property we evaluate.
          </p>
        </div>

        {/* Theory Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-slate-800 rounded-full p-1">
            <button
              onClick={() => setActiveTheory('broken-windows')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTheory === 'broken-windows'
                  ? 'bg-teal-500 text-white'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Broken Windows Model
            </button>
            <button
              onClick={() => setActiveTheory('crime-triangle')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTheory === 'crime-triangle'
                  ? 'bg-teal-500 text-white'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Crime Triangle Theory
            </button>
          </div>
        </div>

        {/* Theory Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {activeTheory === 'broken-windows' ? (
            <>
              <div className="order-2 lg:order-1">
                <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700">
                  <div className="w-14 h-14 flex items-center justify-center bg-teal-500/20 rounded-xl mb-6">
                    <i className="ri-window-line text-2xl text-teal-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">The Broken Windows Model</h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">
                    This criminological theory demonstrates that visible signs of disorder and neglect such as broken windows, 
                    graffiti, or poor lighting signal that an area is unmonitored and vulnerable, inviting further crime and decay.
                  </p>
                  <h4 className="text-lg font-semibold text-teal-400 mb-3">How SafePoint Applies This:</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-400 mt-1"></i>
                      <span className="text-slate-300">Identify visual deterrents and signs of neglect that attract criminal activity</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-400 mt-1"></i>
                      <span className="text-slate-300">Prioritize maintenance issues that impact security perception</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-400 mt-1"></i>
                      <span className="text-slate-300">Create action plans to project an image of vigilance and care</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-400 mt-1"></i>
                      <span className="text-slate-300">Address small issues before they escalate into larger vulnerabilities</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="relative rounded-2xl overflow-hidden h-96">
                  <img
                    src="https://public.readdy.ai/ai/img_res/fd6da4425ecced09d0222cc68167dabb.jpg"
                    alt="Well-maintained property demonstrating broken windows theory"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <p className="text-sm text-slate-300 italic">
                      "A well-maintained property signals vigilance and deters criminal opportunity."
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="order-2 lg:order-1">
                <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700">
                  <div className="w-14 h-14 flex items-center justify-center bg-teal-500/20 rounded-xl mb-6">
                    <i className="ri-triangle-line text-2xl text-teal-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">The Crime Triangle Theory</h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">
                    Also known as the Problem Analysis Triangle, this model states that crime occurs when three elements converge: 
                    a <strong className="text-white">motivated offender</strong>, a <strong className="text-white">suitable target</strong>, 
                    and the <strong className="text-white">absence of a capable guardian</strong>. Remove any one element, and crime is prevented.
                  </p>
                  <h4 className="text-lg font-semibold text-teal-400 mb-3">How SafePoint Applies This:</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-400 mt-1"></i>
                      <span className="text-slate-300">Assess what makes your property a "suitable target" and reduce appeal</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-400 mt-1"></i>
                      <span className="text-slate-300">Implement visible and invisible guardianship measures</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-400 mt-1"></i>
                      <span className="text-slate-300">Analyze offender patterns in your area to anticipate threats</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-400 mt-1"></i>
                      <span className="text-slate-300">Design layered security that disrupts the crime triangle at multiple points</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="relative rounded-2xl overflow-hidden h-96">
                  <img
                    src="https://readdy.ai/api/search-image?query=Security%20surveillance%20control%20room%20with%20multiple%20monitors%20showing%20property%20camera%20feeds%20professional%20security%20operations%20center%20modern%20technology%20blue%20ambient%20lighting%20clean%20professional%20environment%20showing%20guardianship%20and%20monitoring&width=600&height=400&seq=law2&orientation=landscape"
                    alt="Security monitoring demonstrating capable guardianship"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <p className="text-sm text-slate-300 italic">
                      "Capable guardianship breaks the crime triangle and prevents opportunity."
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Crime Triangle Visual */}
        {activeTheory === 'crime-triangle' && (
          <div className="flex justify-center mb-20">
            <div className="relative w-80 h-72">
              {/* Triangle Lines */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 320 280">
                <polygon
                  points="160,20 20,260 300,260"
                  fill="none"
                  stroke="#14b8a6"
                  strokeWidth="2"
                  strokeDasharray="8,4"
                />
              </svg>
              {/* Top Point - Offender */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-red-500/20 rounded-full mx-auto mb-2">
                  <i className="ri-user-warning-line text-xl text-red-400"></i>
                </div>
                <span className="text-sm font-medium text-slate-300">Motivated Offender</span>
              </div>
              {/* Bottom Left - Target */}
              <div className="absolute bottom-0 left-0 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-500/20 rounded-full mx-auto mb-2">
                  <i className="ri-home-gear-line text-xl text-amber-400"></i>
                </div>
                <span className="text-sm font-medium text-slate-300">Suitable Target</span>
              </div>
              {/* Bottom Right - Guardian */}
              <div className="absolute bottom-0 right-0 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-full mx-auto mb-2">
                  <i className="ri-shield-user-line text-xl text-teal-400"></i>
                </div>
                <span className="text-sm font-medium text-slate-300">Absent Guardian</span>
              </div>
              {/* Center - Crime */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/4 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-slate-700 rounded-full mx-auto mb-2 border-2 border-slate-600">
                  <i className="ri-error-warning-line text-2xl text-slate-400"></i>
                </div>
                <span className="text-xs font-medium text-slate-400">CRIME<br/>OCCURS</span>
              </div>
            </div>
          </div>
        )}

        {/* Crime Mapping Intelligence */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">Crime Mapping Intelligence</h3>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="relative h-80 rounded-xl overflow-hidden shadow-2xl">
              <img
                src="https://static.readdy.ai/image/ae118e97ebc078ab009b24ac4999483c/a64bfab8e382af6c05ffcc611750797e.jpeg"
                alt="Crime Mapping Heat Map Analysis"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="grid gap-6">
              <div className="bg-slate-700/50 p-6 rounded-lg border border-teal-500/20">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-teal-400"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Incident Tracking</h4>
                    <p className="text-slate-300 text-sm">Monitor crime patterns by type, location, and frequency to identify emerging threats</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-700/50 p-6 rounded-lg border border-teal-500/20">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-fire-line text-2xl text-teal-400"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Hotspot Analysis</h4>
                    <p className="text-slate-300 text-sm">Identify high-risk areas requiring enhanced security measures and patrol focus</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-700/50 p-6 rounded-lg border border-teal-500/20">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-2xl text-teal-400"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Trend Forecasting</h4>
                    <p className="text-slate-300 text-sm">Predict future crime patterns to proactively deploy resources and prevent incidents</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Research Section */}
        <div className="bg-gradient-to-br from-teal-900/40 to-slate-800/40 rounded-3xl p-8 md:p-12 border border-teal-700/30">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 rounded-full mb-6">
                <i className="ri-radar-line text-teal-400"></i>
                <span className="text-sm font-medium text-teal-400">Advanced Intelligence</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Beyond Your Property Line
              </h3>
              <p className="text-slate-300 mb-6 leading-relaxed">
                SafePoint doesn't just assess your property we conduct comprehensive research on the surrounding area 
                to give you a complete picture of your security landscape. Our law enforcement background enables 
                access to insights most consultants simply cannot provide.
              </p>
              
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">Our Research Includes:</h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 bg-slate-800/50 rounded-xl p-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-teal-400"></i>
                    </div>
                    <div>
                      <h5 className="font-medium text-white text-sm">Neighborhood Crime Analysis</h5>
                      <p className="text-xs text-slate-400 mt-1">Historical crime data and trend patterns</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-slate-800/50 rounded-xl p-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-route-line text-teal-400"></i>
                    </div>
                    <div>
                      <h5 className="font-medium text-white text-sm">Access Point Mapping</h5>
                      <p className="text-xs text-slate-400 mt-1">Entry/exit routes and traffic patterns</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-slate-800/50 rounded-xl p-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-building-4-line text-teal-400"></i>
                    </div>
                    <div>
                      <h5 className="font-medium text-white text-sm">Surrounding Property Review</h5>
                      <p className="text-xs text-slate-400 mt-1">Adjacent land use and risk factors</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-slate-800/50 rounded-xl p-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-police-car-line text-teal-400"></i>
                    </div>
                    <div>
                      <h5 className="font-medium text-white text-sm">Law Enforcement Insights</h5>
                      <p className="text-xs text-slate-400 mt-1">Response times and patrol coverage</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-slate-800/50 rounded-xl p-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-community-line text-teal-400"></i>
                    </div>
                    <div>
                      <h5 className="font-medium text-white text-sm">Community Risk Factors</h5>
                      <p className="text-xs text-slate-400 mt-1">Social dynamics and environmental concerns</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-slate-800/50 rounded-xl p-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-lightbulb-flash-line text-teal-400"></i>
                    </div>
                    <div>
                      <h5 className="font-medium text-white text-sm">Infrastructure Assessment</h5>
                      <p className="text-xs text-slate-400 mt-1">Street lighting, visibility, and blind spots</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-2xl overflow-hidden h-80 lg:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20suburban%20neighborhood%20with%20residential%20properties%20streets%20and%20surrounding%20area%20showing%20urban%20planning%20perspective%20drone%20photography%20style%20clear%20day%20showing%20property%20boundaries%20and%20access%20routes%20professional%20real%20estate%20mapping&width=600&height=450&seq=law3&orientation=landscape"
                  alt="Aerial neighborhood analysis"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-slate-800 rounded-xl p-5 border border-slate-700 shadow-xl max-w-xs">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-500 rounded-lg">
                    <i className="ri-search-eye-line text-white text-lg"></i>
                  </div>
                  <span className="font-semibold text-white">360° Security View</span>
                </div>
                <p className="text-sm text-slate-400">
                  We analyze threats from every angle—not just what's on your property, but what surrounds it.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-slate-400 mb-6">
            Ready to see your property through the eyes of law enforcement?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shield-check-line"></i>
            Request a Security Assessment
          </a>
        </div>
      </div>
    </section>
  );
}
